import { create } from 'zustand';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type MutableRefObject,
} from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import NewAnalyticsApi from 'api/new-analytics';
import { queryKey } from 'constants/queryKey';
import { ApiClientError } from 'api/client';
import { useAnalyticsFiltersStore } from 'app/modules/analytics/store/analyticsFiltersStore';
import { useAnalyticsSelection } from 'app/modules/analytics/hooks/useAnalyticsSelection';
import { useAnalyticsInventory } from 'app/modules/analytics/hooks/useAnalyticsInventory';
import { useAnalyticsHealthQuery } from 'app/modules/analytics/hooks/useAnalyticsHealthQuery';
import Logger from 'utils/logger';
import TimeUtils from 'app/utils/time';
import { useAnalyticsAutoSyncMemory } from 'app/modules/analytics/hooks/useAnalyticsAutoSyncMemory';
import { computeAnalyticsAutoSyncSelectionKey } from 'app/modules/analytics/utils/analyticsAutoSyncSelectionKey';
import { formatAnalyticsSyncRunLabel } from 'app/modules/analytics/utils/formatAnalyticsSyncRunLabel';
import { notifyAnalyticsSyncFinished } from 'app/modules/analytics/utils/analyticsSyncFinishedToast';
import { normalizeAnalyticsPageIds } from 'app/modules/analytics/utils/normalizeAnalyticsPageIds';
import { invalidateAnalyticsSyncRunQueries } from 'app/modules/analytics/utils/invalidateAnalyticsSyncRunQueries';
import {
  doAnyPageIdsRequireAnalyticsReconnect,
  doesAnalyticsSyncFailureRequireReconnect,
  doesAnalyticsSyncSelectionRequireReconnect,
  doesInFlightSyncRequireReconnect,
  doesTriggerSyncErrorRequireReconnect,
  isPageHealthTokenExpired,
} from 'app/modules/analytics/utils/analyticsSyncReconnect';
import type { SyncBatch } from 'api/new-analytics/types';

const MAX_POLL_DURATION = TimeUtils.calcMilliseconds.minutes(20);
const POLL_INTERVAL = TimeUtils.calcMilliseconds.seconds(3);
const SUCCESS_DISPLAY_DURATION = TimeUtils.calcMilliseconds.seconds(2.5);
const MAX_SYNC_PAGE_IDS_PER_REQUEST = 50;
const EMPTY_PAGE_IDS: readonly number[] = [];

interface TriggerSyncMutationParams {
  pageIds: number[];
  since: string;
  until: string;
}

function chunkPageIds(pageIds: number[]): number[][] {
  const chunks: number[][] = [];
  for (let i = 0; i < pageIds.length; i += MAX_SYNC_PAGE_IDS_PER_REQUEST) {
    chunks.push(pageIds.slice(i, i + MAX_SYNC_PAGE_IDS_PER_REQUEST));
  }
  return chunks;
}

export function computeSyncPageProgressFromBatches(
  data: SyncBatch[] | undefined,
): { completed: number; total: number } | null {
  if (!data?.length) {
    return null;
  }
  const total = data.length;
  const completed = data.filter(
    (batch) => batch.status === 'completed' || batch.status === 'failed',
  ).length;
  return { completed, total };
}

function clearSyncChunkQueue(
  pendingChunksRef: MutableRefObject<number[][]>,
  totalChunksRef: MutableRefObject<number>,
) {
  pendingChunksRef.current = [];
  totalChunksRef.current = 0;
}

function clearPendingSuccessTimer(
  timerRef: MutableRefObject<ReturnType<typeof setTimeout> | null>,
) {
  if (timerRef.current != null) {
    clearTimeout(timerRef.current);
    timerRef.current = null;
  }
}

interface AnalyticsSyncBatchState {
  batchIds: string[];
  startedAt: number | null;
  isShowingSuccess: boolean;
  errorMessage: string | null;
  pendingChunkCount: number;
  syncCompletionConsumptionKey: string | null;
  syncRunSelectionKey: string | null;
  syncRunIsGroup: boolean;
  syncRunPageId: number | null;
  syncRunGroupId: number | null;
  syncRunPageIdsNormalized: readonly number[];
  syncRunSince: string | null;
  syncRunUntil: string | null;
  tryConsumeCompletionOnce: () => boolean;
  setBatchIds: (ids: string[]) => void;
  clearPollingBetweenChunks: () => void;
  setSyncRunSnapshot: (snapshot: {
    selectionKey: string;
    isGroup: boolean;
    pageId: number | null;
    groupId: number | null;
    pageIdsNormalized: number[];
    since: string;
    until: string;
  }) => void;
  reset: () => void;
  setShowingSuccess: (value: boolean) => void;
  setError: (message: string | null) => void;
  setPendingChunkCount: (count: number) => void;
  isPollingActive: () => boolean;
  hasExceededMaxDuration: () => boolean;
}

export const useAnalyticsSyncBatchState = create<AnalyticsSyncBatchState>(
  (set, get) => ({
    batchIds: [],
    startedAt: null,
    isShowingSuccess: false,
    errorMessage: null,
    pendingChunkCount: 0,
    syncCompletionConsumptionKey: null,
    syncRunSelectionKey: null,
    syncRunIsGroup: false,
    syncRunPageId: null,
    syncRunGroupId: null,
    syncRunPageIdsNormalized: [],
    syncRunSince: null,
    syncRunUntil: null,

    tryConsumeCompletionOnce: () => {
      const state = get();
      const key = [...state.batchIds].slice().sort().join('|');
      if (!key) return false;
      if (state.syncCompletionConsumptionKey === key) {
        return false;
      }
      set({ syncCompletionConsumptionKey: key });
      return true;
    },

    setSyncRunSnapshot: (snapshot) => {
      set({
        syncRunSelectionKey: snapshot.selectionKey,
        syncRunIsGroup: snapshot.isGroup,
        syncRunPageId: snapshot.pageId,
        syncRunGroupId: snapshot.groupId,
        syncRunPageIdsNormalized: snapshot.pageIdsNormalized,
        syncRunSince: snapshot.since,
        syncRunUntil: snapshot.until,
      });
    },

    setBatchIds: (batchIds) => {
      if (batchIds.length > 0) {
        set({
          batchIds,
          startedAt: Date.now(),
          errorMessage: null,
        });
        Logger.info('[AnalyticsSync] Polling started', { batchIds });
      } else {
        set({
          batchIds: [],
          startedAt: null,
          syncCompletionConsumptionKey: null,
        });
      }
    },

    clearPollingBetweenChunks: () => {
      set({
        batchIds: [],
        startedAt: null,
        syncCompletionConsumptionKey: null,
      });
    },

    setShowingSuccess: (isShowingSuccess) => set({ isShowingSuccess }),

    setError: (errorMessage) => set({ errorMessage }),

    setPendingChunkCount: (pendingChunkCount) => set({ pendingChunkCount }),

    reset: () => {
      const state = get();
      if (state.batchIds.length > 0) {
        Logger.info('[AnalyticsSync] Polling stopped', {
          batchIds: state.batchIds,
        });
      }
      set({
        batchIds: [],
        startedAt: null,
        isShowingSuccess: false,
        errorMessage: null,
        pendingChunkCount: 0,
        syncCompletionConsumptionKey: null,
        syncRunSelectionKey: null,
        syncRunIsGroup: false,
        syncRunPageId: null,
        syncRunGroupId: null,
        syncRunPageIdsNormalized: [],
        syncRunSince: null,
        syncRunUntil: null,
      });
    },

    isPollingActive: () => {
      const state = get();
      return state.batchIds.length > 0 && !state.hasExceededMaxDuration();
    },

    hasExceededMaxDuration: () => {
      const state = get();
      if (!state.startedAt) return false;
      return Date.now() - state.startedAt > MAX_POLL_DURATION;
    },
  }),
);

export function useAnalyticsSync() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const completionHandledRef = useRef(false);
  const timeoutHandledRef = useRef(false);
  const pendingChunksRef = useRef<number[][]>([]);
  const totalChunksForRunRef = useRef(0);
  const { pages, profileGroups } = useAnalyticsInventory();
  const {
    selectedPage,
    selectedPageId,
    selectedGroupId,
    isGroupSelected,
    enabledGroupPageIds,
    selectPage,
    selectGroup,
  } = useAnalyticsSelection();
  const startDate = useAnalyticsFiltersStore((state) => state.startDate);
  const endDate = useAnalyticsFiltersStore((state) => state.endDate);
  const { getPageHealth } = useAnalyticsHealthQuery();

  const {
    batchIds,
    setBatchIds,
    reset,
    clearPollingBetweenChunks,
    setSyncRunSnapshot,
    isPollingActive,
    hasExceededMaxDuration,
    isShowingSuccess,
    setShowingSuccess,
    setError,
    setPendingChunkCount,
    errorMessage,
    tryConsumeCompletionOnce,
    syncRunSelectionKey,
    syncRunIsGroup,
    syncRunPageId,
    syncRunGroupId,
    syncRunPageIdsNormalized,
    startedAt,
  } = useAnalyticsSyncBatchState();

  const successDisplayTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  useEffect(() => () => clearPendingSuccessTimer(successDisplayTimeoutRef), []);

  const invalidateQueriesForStoredSyncRun = useCallback(() => {
    invalidateAnalyticsSyncRunQueries(
      queryClient,
      useAnalyticsSyncBatchState.getState(),
    );
  }, [queryClient]);

  const currentSelectionKey = useMemo(
    () =>
      computeAnalyticsAutoSyncSelectionKey({
        isGroupSelected,
        selectedGroupId,
        selectedPageId,
        enabledGroupPageIds,
      }),
    [isGroupSelected, selectedGroupId, selectedPageId, enabledGroupPageIds],
  );

  const syncTargetMatchesSelection =
    syncRunSelectionKey == null ||
    currentSelectionKey == null ||
    syncRunSelectionKey === currentSelectionKey;

  const jumpToSyncTarget = useCallback(() => {
    const store = useAnalyticsSyncBatchState.getState();
    if (store.syncRunIsGroup && store.syncRunGroupId != null) {
      selectGroup(store.syncRunGroupId);
      return;
    }
    if (store.syncRunPageId != null) {
      const page = pages.find(
        (listPage) => listPage.id === store.syncRunPageId,
      );
      if (page) {
        selectPage(page);
      }
    }
  }, [pages, selectGroup, selectPage]);

  const resolveSyncPageIds = useCallback((): number[] | null => {
    if (isGroupSelected) {
      const ids = Array.from(enabledGroupPageIds);
      if (ids.length === 0) return null;
      return normalizeAnalyticsPageIds(ids);
    }
    if (selectedPageId != null) return [selectedPageId];
    return null;
  }, [isGroupSelected, enabledGroupPageIds, selectedPageId]);

  const abortInFlightSync = useCallback(
    (failedSelectionKey: string | null) => {
      clearSyncChunkQueue(pendingChunksRef, totalChunksForRunRef);
      if (failedSelectionKey != null) {
        useAnalyticsAutoSyncMemory
          .getState()
          .markIssuedSyncForSelectionKey(failedSelectionKey);
      }
      reset();
      queryClient.invalidateQueries(queryKey.analyticsHealth());
    },
    [queryClient, reset],
  );

  const triggerSync = useMutation(
    (params: TriggerSyncMutationParams) =>
      NewAnalyticsApi.triggerSync({
        page_ids: params.pageIds,
        since: params.since,
        until: params.until,
      }),
    {
      onSuccess: (batches: SyncBatch[]) => {
        clearPendingSuccessTimer(successDisplayTimeoutRef);
        setShowingSuccess(false);
        completionHandledRef.current = false;
        timeoutHandledRef.current = false;
        const ids = batches.map((batch) => batch.batch_id);
        setBatchIds(ids);
      },
      onError: (e: ApiClientError, variables: TriggerSyncMutationParams) => {
        const failedSelectionKey =
          useAnalyticsSyncBatchState.getState().syncRunSelectionKey;
        const isReconnectError = doesTriggerSyncErrorRequireReconnect({
          pageIds: variables.pageIds,
          userMessage: e?.userMessage,
          getPageHealth,
        });
        abortInFlightSync(failedSelectionKey);
        if (isReconnectError) {
          return;
        }
        setError(e?.userMessage ?? t('somethingWentWrong'));
      },
    },
  );

  const triggerSyncMutate = triggerSync.mutate;

  const batchesQuery = useQuery(
    queryKey.analyticsSyncBatch(batchIds),
    () =>
      Promise.all(
        batchIds.map((batchId) => NewAnalyticsApi.getSyncBatchStatus(batchId)),
      ),
    {
      enabled: batchIds.length > 0 && isPollingActive(),
      refetchInterval: (data: SyncBatch[] | undefined) => {
        if (hasExceededMaxDuration()) {
          if (!timeoutHandledRef.current) {
            timeoutHandledRef.current = true;
            Logger.warn('[AnalyticsSync] Max poll duration exceeded', {
              batchIds,
            });
            clearPendingSuccessTimer(successDisplayTimeoutRef);
            clearSyncChunkQueue(pendingChunksRef, totalChunksForRunRef);
            reset();
            setError(t('analyticsSyncTimedOut'));
          }
          return false;
        }
        if (!data?.length) return POLL_INTERVAL;
        const allDone = data.every(
          (batch) => batch.status === 'completed' || batch.status === 'failed',
        );
        if (allDone) return false;
        return POLL_INTERVAL;
      },
      onSuccess: (data: SyncBatch[]) => {
        if (!data.length || completionHandledRef.current) return;
        const allDone = data.every(
          (batch) => batch.status === 'completed' || batch.status === 'failed',
        );
        if (!allDone) return;
        completionHandledRef.current = true;
        const anyFailed = data.some((batch) => batch.status === 'failed');
        if (anyFailed) {
          const failedBatch = data.find((batch) => batch.status === 'failed');
          const isReconnectError =
            failedBatch != null &&
            doesAnalyticsSyncFailureRequireReconnect({
              pageId: failedBatch.page_id,
              errorDetails: failedBatch.error_details,
              getPageHealth,
            });
          clearPendingSuccessTimer(successDisplayTimeoutRef);
          const failedSelectionKey =
            useAnalyticsSyncBatchState.getState().syncRunSelectionKey;
          abortInFlightSync(failedSelectionKey);
          invalidateQueriesForStoredSyncRun();
          if (!isReconnectError) {
            setError(t('analyticsSyncFailed'));
          }
          return;
        }

        const nextChunk = pendingChunksRef.current.shift();
        if (nextChunk) {
          completionHandledRef.current = false;
          setPendingChunkCount(pendingChunksRef.current.length);
          clearPendingSuccessTimer(successDisplayTimeoutRef);
          clearPollingBetweenChunks();
          const store = useAnalyticsSyncBatchState.getState();
          const since = store.syncRunSince ?? startDate.format('YYYY-MM-DD');
          const until = store.syncRunUntil ?? endDate.format('YYYY-MM-DD');
          triggerSync.mutate({ pageIds: nextChunk, since, until });
          return;
        }

        clearSyncChunkQueue(pendingChunksRef, totalChunksForRunRef);
        if (!tryConsumeCompletionOnce()) return;
        invalidateQueriesForStoredSyncRun();
        setShowingSuccess(true);
        const completedRun = useAnalyticsSyncBatchState.getState();
        notifyAnalyticsSyncFinished({
          t,
          targetLabel: formatAnalyticsSyncRunLabel({
            isGroup: completedRun.syncRunIsGroup,
            groupId: completedRun.syncRunGroupId,
            pageId: completedRun.syncRunPageId,
            pageIdsNormalized: completedRun.syncRunPageIdsNormalized,
            pages,
            profileGroups,
            syncBatches: data,
            t,
          }),
        });

        clearPendingSuccessTimer(successDisplayTimeoutRef);
        successDisplayTimeoutRef.current = setTimeout(() => {
          successDisplayTimeoutRef.current = null;
          if (useAnalyticsSyncBatchState.getState().isShowingSuccess) {
            reset();
          }
        }, SUCCESS_DISPLAY_DURATION);
      },
      onError: (error: unknown) => {
        Logger.error(
          '[AnalyticsSync] Status poll failed',
          error instanceof Error ? error.message : String(error),
        );
        clearPendingSuccessTimer(successDisplayTimeoutRef);
        clearSyncChunkQueue(pendingChunksRef, totalChunksForRunRef);
        reset();
        setError(t('analyticsSyncFailed'));
      },
      cacheTime: MAX_POLL_DURATION,
      staleTime: 0,
    },
  );

  const syncTargetLabel = useMemo(() => {
    if (syncRunSelectionKey == null) {
      return '';
    }
    return formatAnalyticsSyncRunLabel({
      isGroup: syncRunIsGroup,
      groupId: syncRunGroupId,
      pageId: syncRunPageId,
      pageIdsNormalized: syncRunPageIdsNormalized,
      pages,
      profileGroups,
      syncBatches: batchesQuery.data ?? [],
      t,
    });
  }, [
    syncRunSelectionKey,
    syncRunIsGroup,
    syncRunGroupId,
    syncRunPageId,
    syncRunPageIdsNormalized,
    pages,
    profileGroups,
    batchesQuery.data,
    t,
  ]);

  const syncPageProgress = useMemo(
    () => computeSyncPageProgressFromBatches(batchesQuery.data),
    [batchesQuery.data],
  );

  const isTerminal =
    batchesQuery.data != null &&
    batchesQuery.data.length > 0 &&
    batchesQuery.data.every(
      (batch) => batch.status === 'completed' || batch.status === 'failed',
    );

  const isSyncing =
    triggerSync.isLoading || (batchIds.length > 0 && !isTerminal);

  const selectionRequiresReconnect = useMemo(
    () =>
      doesAnalyticsSyncSelectionRequireReconnect({
        isGroupSelected,
        selectedPage,
        selectedPageId,
        enabledGroupPageIds,
        getPageHealth,
      }),
    [
      isGroupSelected,
      selectedPage,
      selectedPageId,
      enabledGroupPageIds,
      getPageHealth,
    ],
  );

  const inFlightRequiresReconnect = useMemo(
    () =>
      doesInFlightSyncRequireReconnect(syncRunPageIdsNormalized, getPageHealth),
    [syncRunPageIdsNormalized, getPageHealth],
  );

  useEffect(() => {
    if (!inFlightRequiresReconnect) {
      return;
    }
    if (batchIds.length === 0 && !triggerSync.isLoading) {
      return;
    }
    abortInFlightSync(
      useAnalyticsSyncBatchState.getState().syncRunSelectionKey,
    );
  }, [
    inFlightRequiresReconnect,
    batchIds.length,
    triggerSync.isLoading,
    abortInFlightSync,
  ]);

  const requestSync = useCallback(
    (options?: { userInitiated?: boolean }) => {
      const notifyBlocked = (message: string) => {
        if (options?.userInitiated) {
          setError(message);
        }
      };
      if (isSyncing || isShowingSuccess) {
        return;
      }
      const pageIds = resolveSyncPageIds();
      if (!pageIds?.length) {
        notifyBlocked(t('analyticsSyncNoProfiles'));
        return;
      }
      if (
        selectionRequiresReconnect ||
        doAnyPageIdsRequireAnalyticsReconnect(pageIds, getPageHealth)
      ) {
        const reconnectProfileNames = pageIds
          .filter((pageId) => isPageHealthTokenExpired(getPageHealth(pageId)))
          .map((pageId) => pages.find((page) => page.id === pageId)?.name)
          .filter((name): name is string => Boolean(name));
        notifyBlocked(
          reconnectProfileNames.length > 0
            ? t('analyticsSyncReconnectRequiredForProfiles', {
                profiles: reconnectProfileNames.join(', '),
                defaultValue: 'Reconnect required to sync: {{profiles}}',
              })
            : t('analyticsHealth.bannerTokenExpired'),
        );
        return;
      }
      const chunks = chunkPageIds(pageIds);
      totalChunksForRunRef.current = chunks.length;
      pendingChunksRef.current = chunks.length > 1 ? chunks.slice(1) : [];
      setPendingChunkCount(pendingChunksRef.current.length);
      const selectionKey = computeAnalyticsAutoSyncSelectionKey({
        isGroupSelected,
        selectedGroupId,
        selectedPageId,
        enabledGroupPageIds,
      });
      if (selectionKey == null) {
        notifyBlocked(t('analyticsSyncNoProfiles'));
        return;
      }
      setError(null);

      const since = startDate.format('YYYY-MM-DD');
      const until = endDate.format('YYYY-MM-DD');
      setSyncRunSnapshot({
        selectionKey,
        isGroup: isGroupSelected,
        pageId: isGroupSelected ? null : selectedPageId,
        groupId: isGroupSelected ? selectedGroupId : null,
        pageIdsNormalized: normalizeAnalyticsPageIds(pageIds),
        since,
        until,
      });
      triggerSyncMutate({ pageIds: chunks[0]!, since, until });
    },
    [
      resolveSyncPageIds,
      setError,
      setPendingChunkCount,
      t,
      pages,
      triggerSyncMutate,
      isShowingSuccess,
      isSyncing,
      startDate,
      endDate,
      setSyncRunSnapshot,
      isGroupSelected,
      selectedGroupId,
      selectedPageId,
      enabledGroupPageIds,
      selectionRequiresReconnect,
      getPageHealth,
    ],
  );

  const cancelSync = useCallback(() => {
    abortInFlightSync(
      useAnalyticsSyncBatchState.getState().syncRunSelectionKey,
    );
  }, [abortInFlightSync]);

  const dismissSyncError = useCallback(() => {
    setError(null);
  }, [setError]);

  const status: 'idle' | 'syncing' | 'success' | 'error' = errorMessage
    ? 'error'
    : isShowingSuccess
    ? 'success'
    : isSyncing
    ? 'syncing'
    : 'idle';

  const syncingPageIds = isSyncing ? syncRunPageIdsNormalized : EMPTY_PAGE_IDS;

  return {
    requestSync,
    cancelSync,
    isSyncing,
    isShowingSuccess,
    status,
    syncError: errorMessage,
    syncingPageIds,
    syncPageProgress,
    syncTargetMatchesSelection,
    syncTargetLabel,
    syncTargetIsGroup: syncRunIsGroup,
    syncStartedAt: startedAt,
    jumpToSyncTarget,
    dismissSyncError,
  };
}
